import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useNotificationDetail } from '../hooks/useNotificationDetail';
import { useResendNotification } from '../hooks/useResendNotification';
import type { NotificationStatus, ApiError } from '../types/notificationCenter';

interface NotificationHistoryDetailDrawerProps {
  historyId: number | null;
  onClose: () => void;
  onResendSuccess?: (toast: string) => void;
  onResendError?: (toast: string) => void;
}

function statusTone(status: NotificationStatus): StatusTone {
  if (status === 'SENT') return 'success';
  if (status === 'PENDING') return 'warning';
  return 'danger';
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  });
}

/** Drawer 내부 요약 행 */
function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-2) 0',
        borderBottom: '1px dashed var(--color-surface-border)',
        fontSize: 'var(--font-size-sm)',
        alignItems: 'start',
      }}
    >
      <span style={{ color: 'var(--color-text-secondary)', fontWeight: 500, fontSize: 'var(--font-size-xs)', paddingTop: 2 }}>
        {label}
      </span>
      <span style={{ color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>{children}</span>
    </div>
  );
}

/** Drawer 스켈레톤 */
function DrawerSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
      {[1, 2].map((n) => (
        <div
          key={n}
          style={{
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
          }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 12,
                width: `${55 + i * 8}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                marginBottom: 'var(--spacing-3)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * NotificationHistoryDetailDrawer — 발송 이력 상세 우측 슬라이드 drawer
 * role=dialog · aria-modal=true · focus trap · ESC 닫기
 */
export function NotificationHistoryDetailDrawer({
  historyId,
  onClose,
  onResendSuccess,
  onResendError,
}: NotificationHistoryDetailDrawerProps) {
  const { t } = useTranslation();
  const detailQuery = useNotificationDetail(historyId);
  const resendMutation = useResendNotification();
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);

  const detail = detailQuery.data;

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (resendConfirmOpen) setResendConfirmOpen(false);
        else onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, resendConfirmOpen]);

  // 드로어 열릴 때 닫기 버튼에 포커스
  useEffect(() => {
    if (historyId !== null) {
      setTimeout(() => closeRef.current?.focus(), 50);
    }
  }, [historyId]);

  // focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const focusables = drawerRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleResendConfirm = useCallback(() => {
    if (historyId === null) return;
    resendMutation.mutate(historyId, {
      onSuccess: () => {
        setResendConfirmOpen(false);
        onResendSuccess?.(t('notificationCenter.toast.resendSuccess'));
      },
      onError: (err) => {
        setResendConfirmOpen(false);
        const apiErr = err as unknown as ApiError;
        const code = apiErr?.code ?? '';
        const errKey = `notificationCenter.error.${code}`;
        const msg = t(errKey, { defaultValue: t('notificationCenter.toast.resendFailed') });
        onResendError?.(msg);
      },
    });
  }, [historyId, resendMutation, onResendSuccess, onResendError, t]);

  if (historyId === null) return null;

  const isResendable = detail?.status === 'FAILED';

  return (
    <>
      {/* 배경 오버레이 */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-overlay-backdrop)',
          zIndex: 200,
        }}
      />

      {/* 드로어 패널 */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('notificationCenter.detail.title')}
        aria-describedby="notif-drawer-desc"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(520px, 90vw)',
          background: 'var(--color-surface-muted)',
          borderLeft: '1px solid var(--color-surface-border)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4) var(--spacing-6)',
            background: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-surface-border)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {t('notificationCenter.detail.title')}
            {detail && (
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  background: 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                #{detail.id}
              </span>
            )}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label={t('notificationCenter.detail.button.close')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* 본문 */}
        <div
          id="notif-drawer-desc"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--spacing-4) var(--spacing-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* Loading */}
          {detailQuery.isLoading && <DrawerSkeleton />}

          {/* Error */}
          {detailQuery.isError && (
            <div
              role="alert"
              style={{
                padding: 'var(--spacing-4)',
                background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
                border: '1px solid var(--color-danger)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-danger)',
              }}
            >
              {t('notificationCenter.detail.error.loadFailed')}
              <button
                type="button"
                onClick={() => void detailQuery.refetch()}
                style={{
                  marginLeft: 'var(--spacing-3)',
                  background: 'none',
                  border: '1px solid var(--color-danger)',
                  color: 'var(--color-danger)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                {t('notificationCenter.detail.button.retry')}
              </button>
            </div>
          )}

          {/* Default — detail 로드 완료 */}
          {detail && (
            <>
              {/* CRITICAL/WARN severity 표시 띠 */}
              {(detail.status === 'FAILED') && (
                <div
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
                    borderLeft: '3px solid var(--color-danger)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-danger)',
                    fontWeight: 500,
                  }}
                  role="status"
                >
                  {t('notificationCenter.error.NOTIF-409-001')} — {t('notificationCenter.detail.button.resend')} 가능
                </div>
              )}

              {/* 요약 섹션 */}
              <section
                aria-labelledby="notif-drawer-summary"
                style={{
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-4)',
                }}
              >
                <h3
                  id="notif-drawer-summary"
                  style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, margin: '0 0 var(--spacing-3)' }}
                >
                  {t('notificationCenter.detail.field.status')}
                </h3>

                <DrawerRow label={t('notificationCenter.detail.field.channel')}>
                  <StatusPill tone="neutral">{detail.channel}</StatusPill>
                </DrawerRow>

                <DrawerRow label={t('notificationCenter.detail.field.target')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                    {detail.maskedTarget}
                  </span>
                </DrawerRow>

                {detail.titlePreview && (
                  <DrawerRow label={t('notificationCenter.detail.field.title')}>
                    {detail.titlePreview}
                  </DrawerRow>
                )}

                <DrawerRow label={t('notificationCenter.detail.field.status')}>
                  <StatusPill tone={statusTone(detail.status)}>
                    {t(`notificationCenter.status.${detail.status}`)}
                  </StatusPill>
                </DrawerRow>

                <DrawerRow label={t('notificationCenter.detail.field.attempts')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                    {detail.attemptCount}
                  </span>
                </DrawerRow>

                <DrawerRow label={t('notificationCenter.detail.field.lastAttempt')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                    {formatDateTime(detail.lastAttemptAt)} KST
                  </span>
                </DrawerRow>

                {detail.errorMessage && (
                  <DrawerRow label={t('notificationCenter.detail.field.error')}>
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-danger)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {detail.errorMessage}
                    </span>
                  </DrawerRow>
                )}

                <DrawerRow label={t('notificationCenter.detail.field.providerMessageId')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {detail.providerMessageId ?? '-'}
                  </span>
                </DrawerRow>

                <DrawerRow label={t('notificationCenter.detail.field.createdAt')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                    {formatDateTime(detail.createdAt)} KST
                  </span>
                </DrawerRow>
              </section>

              {/* Payload JSON 뷰어 */}
              <section
                aria-labelledby="notif-drawer-payload"
                style={{
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={payloadOpen}
                  onClick={() => setPayloadOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPayloadOpen((v) => !v);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    cursor: 'pointer',
                    background: 'var(--color-surface-muted)',
                    borderBottom: payloadOpen ? '1px solid var(--color-surface-border)' : undefined,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-brand-primary)',
                      transition: 'transform 0.2s',
                      display: 'inline-block',
                      transform: payloadOpen ? 'none' : 'rotate(-90deg)',
                    }}
                    aria-hidden="true"
                  >
                    ▼
                  </span>
                  <h3 id="notif-drawer-payload" style={{ margin: 0, fontSize: 'inherit', fontWeight: 'inherit' }}>
                    {t('notificationCenter.detail.section.payload')}
                  </h3>
                </div>

                {payloadOpen && (
                  <pre
                    style={{
                      margin: 0,
                      padding: 'var(--spacing-4)',
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-success)',
                      background: 'var(--color-surface-elevated)',
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: 240,
                      overflowY: 'auto',
                    }}
                  >
                    {/* payload 필드는 이력 상세에 미포함(C077 DTO 한계) — createdAt 등 메타 표시 */}
                    {JSON.stringify(
                      {
                        id: detail.id,
                        channel: detail.channel,
                        maskedTarget: detail.maskedTarget,
                        titlePreview: detail.titlePreview,
                        status: detail.status,
                        attemptCount: detail.attemptCount,
                        sentAt: detail.sentAt,
                        providerMessageId: detail.providerMessageId,
                      },
                      null,
                      2
                    )}
                  </pre>
                )}
              </section>
            </>
          )}
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-elevated)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--spacing-2)',
            flexShrink: 0,
          }}
        >
          {/* 재발송 버튼 — FAILED 만 활성 */}
          {detail && (
            <button
              type="button"
              onClick={() => setResendConfirmOpen(true)}
              disabled={!isResendable || resendMutation.isPending}
              aria-disabled={!isResendable}
              title={!isResendable ? t('notificationCenter.error.NOTIF-409-001') : undefined}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: isResendable && !resendMutation.isPending
                  ? 'var(--color-brand-primary)'
                  : 'var(--color-surface-muted)',
                color: isResendable && !resendMutation.isPending
                  ? 'var(--color-text-inverse)'
                  : 'var(--color-text-muted)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isResendable && !resendMutation.isPending ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--font-family-sans)',
                opacity: isResendable ? 1 : 0.5,
              }}
            >
              {resendMutation.isPending ? '...' : t('notificationCenter.detail.button.resend')}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('notificationCenter.detail.button.close')}
          </button>
        </div>
      </div>

      {/* 재발송 확인 다이얼로그 */}
      {resendConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="resend-confirm-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 2100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 400,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="resend-confirm-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('notificationCenter.detail.resendConfirm')}
            </h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-5)' }}>
              {detail?.titlePreview ?? detail?.maskedTarget}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setResendConfirmOpen(false)}
                disabled={resendMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('notificationCenter.detail.resendConfirm.cancel')}
              </button>
              <button
                type="button"
                onClick={handleResendConfirm}
                disabled={resendMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: resendMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: resendMutation.isPending ? 0.6 : 1,
                }}
              >
                {resendMutation.isPending ? '...' : t('notificationCenter.detail.resendConfirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
